/* eslint-disable react/jsx-curly-newline */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useHistory } from 'react-router-dom'

import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  FiCheck,
  FiCheckCircle,
  FiDollarSign,
  FiPlus,
  FiMinus,
} from 'react-icons/fi'
import { FaPlus, FaMinus } from 'react-icons/fa'
import { FcMoneyTransfer, FcBullish } from 'react-icons/fc'
import { addMonths } from 'date-fns'
import { count } from 'console'
import simulationYearsInfo from '../../assets/simulation-years-info.svg'
import simulationPercentageInfo from '../../assets/simulation-percentage-info.svg'
import simulationSelectTimeIcon from '../../assets/simulation-select-time-icon.svg'
import simulationSelectPercentIcon from '../../assets/simulation-select-percent-icon.svg'

import { formatValueReduced, formatValue } from '../../utils/formatValues'
import usePersistedState from '../../hooks/usePersistedState'
import { Valor } from '../../utils/masks'

import Header from '../../components/Header'
import Button from '../../components/Button'

import {
  Container,
  Content,
  GraphWrapper,
  BoxButtons,
  ColoredBoxInfo,
  ReceiveBox,
  ButtonSelectBox,
  ButtonSimulationCalc,
  InfoValuesBox,
  ButtonSimulationValue,
  TextContainer,
  InputSimulationValue,
} from './styles'
import { UserDetails } from '../../utils/interfaces'

interface CustomTooltipInterface {
  active: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any
  label: string
}

interface Values {
  year: number
  invested: number
  finalBalance: number
  rentability: number
}

const Simulation: React.FC = () => {
  const [userDetails] = usePersistedState<UserDetails>(
    'userDetails',
    {} as UserDetails,
  )
  const [selectedReceive, setSelectedReceive] = usePersistedState<
    'PD' | 'PS' | null
  >('receiveTypeSelected', null)

  const [, setTotalRentability] = usePersistedState('totalRentability', 0)
  const [timeValueYears, setTimeValueYears] = usePersistedState<number>(
    'TimeValueYears',
    120,
  )
  const [percentualValuePercent, setPercentualValuePercent] = usePersistedState<
    number
  >('PercentualValuePercent', 0.5)

  const [displayPercent, setDisplayPercent] = useState(false)
  const [displayTime, setDisplayTime] = useState(false)

  const [values, setValues] = useState<Values[]>([])
  const [totalInvested, setTotalInvested] = useState(0)
  const [totalBalance, setTotalBalance] = useState(0)
  const [launched, setLaunched] = useState(false)
  const [totalInvestedNoTaxes, setTotalInvestedNoTaxes] = useState(0)

  const history = useHistory()

  useEffect(() => setTotalRentability((totalBalance * 100) / totalInvested), [
    totalInvested,
    totalBalance,
    setTotalRentability,
  ])

  const percentualValue = useMemo(
    () => formatValue(totalBalance * (percentualValuePercent / 100)),
    [percentualValuePercent, totalBalance],
  )
  const timeValue = useMemo(() => formatValue(totalBalance / timeValueYears), [
    timeValueYears,
    totalBalance,
  ])

  const createValues = useCallback(() => {
    const valuesCalculated: Values[] = []
    let totalInvestedTemp = totalInvested
    let temporaryFinalBalance = 0 + userDetails.aport

    let contBasicaPartic = userDetails.contribuicaoBasica
    const totalMonthsInvested = userDetails.years * 12
    // const rentability = 0.256354466262909
    const rentability = 0.333802953830
    let taxDeduction = 0
    let finalBalance = 0
    let counter = 1

    if (contBasicaPartic >= 80 && contBasicaPartic <= 100) {
      taxDeduction = 0.03
    } else if (contBasicaPartic > 100 && contBasicaPartic <= 150) {
      taxDeduction = 0.02
    } else {
      taxDeduction = 0
    }

    contBasicaPartic -= contBasicaPartic * taxDeduction

    for (counter; counter <= totalMonthsInvested; counter += 1) {
      totalInvestedTemp += contBasicaPartic

      if (counter === 1) {
        finalBalance =
          (temporaryFinalBalance + contBasicaPartic) * (rentability / 100) +
          (contBasicaPartic + temporaryFinalBalance)
      } else {
        finalBalance =
          temporaryFinalBalance * (rentability / 100) +
          (contBasicaPartic + temporaryFinalBalance)
      }
      temporaryFinalBalance = finalBalance

      const val: Values = {
        // eslint-disable-next-line no-bitwise
        year: addMonths(new Date(), counter).getFullYear(),
        invested: totalInvestedTemp,
        rentability,
        finalBalance,
      }
      valuesCalculated.push(val)
    }

    setTotalInvestedNoTaxes(
      userDetails.contribuicaoBasica * totalMonthsInvested,
    )
    setTotalInvested(totalInvestedTemp)
    setTotalBalance(temporaryFinalBalance)
    setValues(valuesCalculated)
    setLaunched(true)
  }, [totalInvested, userDetails])

  /* --- FORMULA ANTIGA DE CÁLCULO --- */
  // const { contribuicaoBasica, years } = userDetails
  // const actualYear = new Date().getFullYear()
  // const lastYear = actualYear + parseInt(years.toString(), 10)

  // const createValues = useCallback(() => {
  //   const valuesCalculated: Values[] = []
  //   const investedByYear = 12 * contribuicaoBasica

  //   let temporaryFinalBalance = 0

  //   for (let i = actualYear; i <= lastYear; i += 1) {
  //     const yearInvested = investedByYear
  //     const rentability = 0.2563545

  //     const finalBalance =
  //       temporaryFinalBalance +
  //       yearInvested +
  //       (temporaryFinalBalance + yearInvested) * (rentability / 100)

  //     temporaryFinalBalance = finalBalance
  //     console.log('counter: ', i)
  //     console.log('finalBalance: ', finalBalance)
  //     const val: Values = {
  //       year: i,
  //       invested: temporaryFinalBalance,
  //       rentability,
  //       finalBalance,
  //     }
  //     valuesCalculated.push(val)
  //   }

  //   setTotalInvested(contribuicaoBasica * parseInt(years.toString(), 10) * 12)
  //   setTotalBalance(temporaryFinalBalance)
  //   setValues(valuesCalculated)
  //   setLaunched(true)
  // }, [actualYear, contribuicaoBasica, lastYear, years])

  useEffect(() => {
    if (!launched) createValues()
  }, [createValues, launched])

  const toggleSelectedReceive = useCallback(
    (selectedReceiveNow: 'PD' | 'PS' | null) => {
      setSelectedReceive(selectedReceiveNow)
      if (selectedReceiveNow === 'PD') {
        setDisplayTime(true)
        setDisplayPercent(false)
      } else if (selectedReceiveNow === 'PS') {
        setDisplayTime(false)
        setDisplayPercent(true)
      } else {
        setDisplayPercent(false)
        setDisplayTime(false)
      }
    },
    [setSelectedReceive],
  )

  function adicPerct() {
    if (percentualValuePercent >= 2.0) {
      setPercentualValuePercent(2.0)
    } else {
      setPercentualValuePercent(percentualValuePercent + 0.05)
    }
  }

  function tiraPerct() {
    if (percentualValuePercent <= 0.2) {
      setPercentualValuePercent(0.2)
    } else {
      setPercentualValuePercent(percentualValuePercent - 0.05)
    }
  }

  function adicAno() {
    if (timeValueYears >= 600) {
      setTimeValueYears(600)
    } else {
      setTimeValueYears(timeValueYears + 1)
    }
  }

  function tiraAno() {
    if (timeValueYears <= 60) {
      setTimeValueYears(60)
    } else {
      setTimeValueYears(timeValueYears - 1)
    }
  }

  function adicAnoPlusTen() {
    if (timeValueYears >= 600) {
      setTimeValueYears(600)
    } else {
      setTimeValueYears(timeValueYears + 10)
    }
  }

  function tiraAnoPlusTen() {
    if (timeValueYears <= 60) {
      setTimeValueYears(60)
    } else {
      setTimeValueYears(timeValueYears - 10)
    }
  }

  const validaTimeValueYears = useCallback(
    value => {
      if (value < 60) {
        setTimeValueYears(60)
        alert('O prazo mínimo é de 60 meses')
      } else if (value > 600) {
        setTimeValueYears(600)
        alert('O prazo máximo é de 600 meses')
      }
    },
    [setTimeValueYears],
  )

  useEffect(() => {
    toggleSelectedReceive(selectedReceive)
  }, [selectedReceive, toggleSelectedReceive])

  const graphValues = values.map(value => ({
    year: value.year,
    value: value.finalBalance,
    formattedValue: Valor(value.finalBalance.toFixed(2)),
  }))

  function CustomTooltip({ active, payload, label }: CustomTooltipInterface) {
    if (active) {
      return (
        <div className="tooltip">
          <h4>Ano: {label}</h4>
          <p>
            Saldo: <span>{Valor(payload[0].value.toFixed(2))}</span>
          </p>
        </div>
      )
    }
    return null
  }

  const handleConfirmValues = useCallback(() => {
    history.push('/register')
  }, [history])

  return (
    <>
      <Header />
      <Container>
        <Content>
          <strong>SIMULAÇÃO DO SALDO PROJETADO NO PLANO SER+</strong>
          <div className="values-box">
            <InfoValuesBox color="blue" gradientDirection="left">
              <FcMoneyTransfer size={28} />
              <span>Valor Total Investido</span>
              <h3>{formatValue(totalInvestedNoTaxes + userDetails.aport)}</h3>
              <small>Suas contribuições no período</small>
            </InfoValuesBox>

            <InfoValuesBox color="blue" gradientDirection="left">
              <FcBullish size={28} />
              <span>Seu saldo projetado</span>
              <h3>{formatValue(totalBalance)}</h3>
              <small>Seu investimento + rentabilidade</small>
            </InfoValuesBox>
          </div>
          <article>
            <p>
              Surpreso com o resultado? <br />
              Navegue no gráfico e acompanhe a evolução do seu patrimônio no
              período desejado.
            </p>
          </article>

          <GraphWrapper>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={graphValues}>
                <defs>
                  <linearGradient id="color" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0072BB44" stopOpacity={0.8} />
                    <stop
                      offset="95%"
                      stopColor="#0072BB88"
                      stopOpacity={0.05}
                    />
                  </linearGradient>
                </defs>

                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#0094D4"
                  fillOpacity={1}
                  fill="url(#color)"
                />

                <Tooltip
                  content={
                    <CustomTooltip active={false} payload={2} label="" />
                  }
                />
                <XAxis name="Ano" dataKey="year" />
                <YAxis
                  name="Saldo"
                  dataKey="value"
                  width={75}
                  axisLine={false}
                  tickLine={false}
                  tickCount={5}
                  tickFormatter={number =>
                    `${formatValueReduced(
                      parseInt(number.toString(), 10),
                      3,
                      'R$',
                    )}`
                  }
                />
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  opacity={0.5}
                />
              </AreaChart>
            </ResponsiveContainer>
          </GraphWrapper>

          <TextContainer>
            <h3>Transparência</h3>
            <p>
              1. Os valores apresentados são apenas estimativas e não garantem
              nenhum direito ao participante. O direito efetivo dependerá dos
              dados reais na data da efetiva concessão do Benefício de Renda
              Mensal ou Temporária do benefício de aposentadoria ou resgate ou
              portabilidade.
            </p>
            <p>
              2. O valor de seu Saldo Projetado foi calculado com base nas
              contribuições sugeridas (liquidas de carregamento) e a aplicação
              de rentabilidade com taxa de juros de 4,08% a.a. e líquida da taxa
              de administração de 0,60% a.a.
            </p>
            <p>
              3. É descontada a taxa de carregamento de 3% para valores de
              contribuição entre R$ 80,00 e R$ 100,00 e de 2% para valores de
              contribuição entre R$ 100,01 e R$ 150,00. Valores acima de R$
              150,00 não há incidência de taxa de carregamento
            </p>
            <p>
              Consulte o Regulamento do seu Plano de Benefícios para maiores
              informações.
            </p>
            <a
              href="https://planosermais.serpros.com.br/wp-content/uploads/2023/06/REGULAMENTO-SER-Atualizado-02-06-23.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <button type="button">Regulamento</button>
            </a>
          </TextContainer>
        </Content>

        <Content>
          <strong>Desejo receber o valor mensalmente via:</strong>

          <ReceiveBox>
            <ButtonSelectBox
              type="button"
              onClick={() => toggleSelectedReceive('PS')}
              selected={selectedReceive === 'PS'}
            >
              <img src={simulationSelectPercentIcon} alt="Percentual" />
              <span>Percentual sobre o saldo</span>
              <FiCheckCircle />
            </ButtonSelectBox>
            <BoxButtons displayed={displayPercent}>
              <ButtonSimulationCalc type="button" onClick={() => tiraPerct()}>
                <FaMinus color="#fff" />
              </ButtonSimulationCalc>
              <ButtonSimulationValue type="button">
                <span>{parseFloat(percentualValuePercent.toFixed(2))}%</span>
              </ButtonSimulationValue>
              <ButtonSimulationCalc type="button" onClick={() => adicPerct()}>
                <FaPlus color="#fff" />
              </ButtonSimulationCalc>
            </BoxButtons>
            <ColoredBoxInfo
              size="large"
              color="blue"
              gradientDirection="right"
              displayed={displayPercent}
            >
              <div>
                <h3>{percentualValue}*</h3>
                <small>Você receberá por mês</small>
                <p>
                  *Valor simulado referente a{' '}
                  {percentualValuePercent.toFixed(2)}% do seu saldo projetado,
                  com pagamentos mensais efetuados com base no percentual que
                  escolher no momento da aposentadoria. <br />
                  Esgotado o saldo da conta de benefício concedido, cessam os
                  pagamentos de benefícios pelo plano.
                </p>
              </div>
              {/* <img src={simulationPercentageInfo} alt="Percentual" /> */}
            </ColoredBoxInfo>
          </ReceiveBox>

          <ReceiveBox>
            <ButtonSelectBox
              type="button"
              onClick={() => toggleSelectedReceive('PD')}
              selected={selectedReceive === 'PD'}
            >
              <img src={simulationSelectTimeIcon} alt="Anos" />
              <span>Prazo Determinado</span>
              <FiCheckCircle />
            </ButtonSelectBox>
            <BoxButtons displayed={displayTime}>
              {/* <ButtonSimulationCalc
                type="button"
                onClick={() => tiraAnoPlusTen()}
                disabled={timeValueYears <= 69}
              >
                -10
              </ButtonSimulationCalc>
              <ButtonSimulationCalc type="button" onClick={() => tiraAno()}>
                -
              </ButtonSimulationCalc> */}
              <InputSimulationValue
                type="number"
                value={timeValueYears}
                min={60}
                max={600}
                onChange={e => setTimeValueYears(e.target.valueAsNumber)}
                onBlur={e => validaTimeValueYears(e.target.valueAsNumber)}
              />
              {/* <ButtonSimulationCalc type="button" onClick={() => adicAno()}>
                +
              </ButtonSimulationCalc>
              <ButtonSimulationCalc
                type="button"
                onClick={() => adicAnoPlusTen()}
                disabled={timeValueYears >= 591}
              >
                +10
              </ButtonSimulationCalc> */}
            </BoxButtons>
            <ColoredBoxInfo
              size="large"
              color="green"
              gradientDirection="left"
              displayed={displayTime}
            >
              <div>
                <h3>{timeValue}*</h3>
                <small>Você receberá por mês</small>
                <p>
                  *Valor simulado referente a modalidade de prazo determinado
                  com duração de {timeValueYears} meses, calculado com base no
                  seu saldo projetado. <br />
                  Esgotado o saldo da conta de benefício concedido, cessam os
                  pagamentos de benefícios pelo plano.
                </p>
              </div>
              {/* <img src={simulationYearsInfo} alt="Anos" /> */}
            </ColoredBoxInfo>
          </ReceiveBox>

          <small className="comment">
            Esta simulação não representa garantia de valor de benefício futuro.
          </small>
        </Content>

        <Button
          type="button"
          fontSize="normal"
          color="green"
          width="large"
          disabled={selectedReceive === null}
          onClick={handleConfirmValues}
        >
          <FiCheck size={40} />
          Quero continuar com adesão ao Plano
        </Button>

        <Button
          type="button"
          fontSize="small"
          onClick={() => history.push('/contribution')}
        >
          Quero alterar o valor e simular novamente
        </Button>
      </Container>
    </>
  )
}

export default Simulation
